import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import authService from '../services/authService';
import { track } from '../analytics/tracker';
import { EVT_LOGOUT } from '../analytics/events';

export const AuthContext = createContext();

/**
 * Auth Context Provider
 * Manages authentication state across the app
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    const { user: loggedInUser } = await authService.login(credentials);
    setUser(loggedInUser);
    return loggedInUser;
  };

  const register = async (userData) => {
    const response = await authService.register(userData);
    const { user: registeredUser, accessToken, refreshToken, coinsAwarded } = response.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(registeredUser));

    setUser(registeredUser);
    return { user: registeredUser, coinsAwarded: coinsAwarded || 0, message: response.message };
  };

  const otpLogin = async (data) => {
    const { user: loggedInUser } = await authService.otpLogin(data);
    setUser(loggedInUser);
    return loggedInUser;
  };

  const logout = () => {
    track(EVT_LOGOUT, {});
    authService.logout();
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    // Update user in context state
    setUser(updatedUserData);
    // Update user in localStorage
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  const value = {
    user,
    loading,
    login,
    otpLogin,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
